import React, { useEffect, useMemo, useState } from 'react';
import styles from 'src/app/surveys/views/new-survey-design/NewSurveyDesign.module.scss';
import Autocomplete from 'src/shared/components/autocomplete/Autocomplete';
import Input from 'src/shared/components/input/Input';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import { Search } from 'lucide-react';
import { countries } from 'src/shared/data/countries';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { useCustomerProjectsInBCQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import useAddSiteToPlatform from '../../hooks/useAddSiteToPlatform';

/**
 * Form component for collecting basic information when adding sites to the platform.
 * Supports creating new customers/projects or adding sites to existing projects.
 * Form behavior adapts based on Portfolio ID presence.
 */
const BasicInformationAddSite = () => {
    const { allCustomers } = useCustomer();
    const { formState, onFormChange } = useAddSiteToPlatform();

    const [customerSearchText, setCustomerSearchText] = useState('');
    const [projectSearchText, setProjectSearchText] = useState('');
    const [countrySearchText, setCountrySearchText] = useState('');

    const [selectedProjectName, setSelectedProjectName] = useState<string>('');
    const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

    const customerOptions = useMemo(() => {
        const customers = allCustomers?.customers || [];
        return ['', 'Create New', ...customers.map(c => c.customerName)];
    }, [allCustomers]);

    const selectedCustomer = useMemo(() => {
        if (!allCustomers?.customers) {
            return null;
        }
        return allCustomers.customers.find(c => c.customerId === selectedCustomerId) || null;
    }, [allCustomers, selectedCustomerId]);

    const { data: projectsData } = useCustomerProjectsInBCQuery({ customerId: selectedCustomerId || '' }, { skip: !selectedCustomerId });

    const hasPortfolioId = Boolean(formState.portfolioId);
    const isCreateNewCustomer = selectedCustomerName === 'Create New' || (!formState.customerId && Boolean(formState.customerName));

    const projectNameOptions = useMemo(() => {
        if (isCreateNewCustomer) {
            return ['Create New'];
        }

        const names = projectsData?.customerProjectsInBC.projects?.map(p => p.projectName) || [];
        return ['Create New', ...names];
    }, [projectsData, isCreateNewCustomer]);

    // Auto-selects "Create New" project when creating new customer or when Portfolio ID exists
    useEffect(() => {
        if (isCreateNewCustomer) {
            setSelectedProjectName('Create New');
            setProjectSearchText('Create New');
            onFormChange({ projectId: '', projectName: '', isHlcaProject: hasPortfolioId ? undefined : true });
            return;
        }

        if (hasPortfolioId && !selectedProjectName) {
            setSelectedProjectName('Create New');
            setProjectSearchText('Create New');
            return;
        }
    }, [hasPortfolioId, selectedProjectName, isCreateNewCustomer, onFormChange]);

    useEffect(() => {
        if (!selectedCustomer?.customerId) {
            return;
        }
        onFormChange({ customerId: selectedCustomer.customerId });
    }, [selectedCustomer, onFormChange]);

    const onCustomerChange = (customerName: string | null) => {
        if (!customerName) {
            return;
        }

        setSelectedCustomerName(customerName);
        setCustomerSearchText(customerName);

        if (customerName === 'Create New') {
            onFormChange({ customerId: '', customerName: '', projectId: '', projectName: '' });
            setSelectedCustomerId('');
            setSelectedProjectName('Create New');
            setProjectSearchText('Create New');
            return;
        }

        const found = allCustomers?.customers?.find(c => c.customerName === customerName);
        if (!found) {
            return;
        }

        setSelectedCustomerId(found.customerId);
        onFormChange({ customerId: found.customerId, customerName: '', projectId: '', projectName: '' });
        setSelectedProjectName('');
        setProjectSearchText('');
    };

    const onProjectSelectionChange = (projectName: string | null) => {
        if (!projectName) {
            return;
        }

        setSelectedProjectName(projectName);
        setProjectSearchText(projectName);

        if (projectName === 'Create New') {
            onFormChange({ projectId: '', projectName: '', isHlcaProject: hasPortfolioId ? undefined : true });
            setProjectSearchText('Create New');
            return;
        }

        const selectedProjectData = projectsData?.customerProjectsInBC.projects.find(entry => entry.projectName === projectName);
        if (!selectedProjectData) {
            return;
        }

        onFormChange({
            projectId: selectedProjectData.projectId,
            projectName: selectedProjectData.projectName,
            country: selectedProjectData.country,
            // For existing projects, isHlcaProject is not needed as backend already knows
            // Only set it when creating a new project
        });
    };

    const onCountryChange = (value: string | null) => {
        onFormChange({ country: value || '' });
    };

    const isCreateNewProject = selectedProjectName === 'Create New' || (!formState.projectId && Boolean(formState.projectName));

    const modeTitle = isCreateNewCustomer
        ? 'New customer + new project'
        : isCreateNewProject
        ? 'New project under existing customer'
        : formState.projectId
        ? 'Add sites to existing project'
        : 'Select customer and project';

    const modeDescription = isCreateNewCustomer
        ? 'You selected Create New for customer. We will create a new customer and a new project.'
        : isCreateNewProject
        ? 'You are creating a new project under the selected customer.'
        : formState.projectId
        ? 'You will add sites to the selected existing project.'
        : 'Choose a customer, then either select a project or choose Create New to make one.';

    const isCustomerIdDisabled = false;
    const isProjectIdDisabled = !selectedCustomerId && !isCreateNewCustomer;
    const isProjectNameDisabled = !isCreateNewProject;
    const isProjectNameRequired = isCreateNewProject;

    // Auto-sets HLCA project flag to true when no Portfolio ID is provided
    useEffect(() => {
        if (!hasPortfolioId) {
            onFormChange({ isHlcaProject: true });
        }
    }, [hasPortfolioId, onFormChange]);

    const onHlcaChange = (checked: boolean) => {
        onFormChange({ isHlcaProject: checked });
    };

    const onProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onFormChange({ projectName: e.target.value });
    };

    const onCustomerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onFormChange({ customerName: e.target.value });
    };

    const onPortfolioIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onFormChange({ portfolioId: e.target.value });
    };

    return (
        <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Basic information</div>
            <div className='bg-grey-0 border border-grey-20 rounded-md p-3 mt-2 mb-3'>
                <div className='text-xs font-semibold text-grey-100 mb-1'>{modeTitle}</div>
                <div className='text-xs text-grey-80'>{modeDescription}</div>
            </div>

            <div className={styles.formInputSection}>
                <div className={styles.formInputSectionTitle}>
                    Customer ID
                    {!hasPortfolioId && <span className='text-red-100'> *</span>}
                </div>
                <Autocomplete
                    options={customerOptions}
                    placeholder={'Select customer or choose &quot;Create New&quot;'}
                    prefixIcon={<Search />}
                    onChange={onCustomerChange}
                    onInputChange={setCustomerSearchText}
                    inputValue={customerSearchText}
                    value={selectedCustomerName || selectedCustomer?.customerName || ''}
                    disabled={isCustomerIdDisabled}
                />
                <div className='text-xs text-grey-80 mt-1'>
                    Select an existing customer or choose &quot;Create New&quot; to add a new one.
                </div>
                {hasPortfolioId && <div className='text-xs text-grey-80 mt-1'>Optional when Portfolio ID is provided</div>}
            </div>

            {isCreateNewCustomer && (
                <div className={styles.formInputSection}>
                    <div className={styles.formInputSectionTitle}>
                        Customer Name
                        <span className='text-red-100'> *</span>
                    </div>
                    <Input value={formState.customerName || ''} onChange={onCustomerNameChange} placeholder='Enter customer name' />
                    <div className='text-xs text-grey-80 mt-1'>Required when creating a new customer</div>
                </div>
            )}

            <div className={styles.formInputSection}>
                <div className={styles.formInputSectionTitle}>Portfolio ID</div>
                <Input value={formState.portfolioId || ''} onChange={onPortfolioIdChange} placeholder='Enter portfolio ID (optional)' />
            </div>

            <div className={styles.formInputSection}>
                <div className={styles.formInputSectionTitle}>
                    Project ID
                    {!hasPortfolioId && <span className='text-red-100'> *</span>}
                </div>
                <Autocomplete
                    options={['', ...projectNameOptions]}
                    placeholder={
                        isCreateNewCustomer
                            ? 'Create New (default)'
                            : hasPortfolioId
                            ? 'Search or select project (optional)'
                            : 'Search or select project'
                    }
                    prefixIcon={<Search />}
                    onChange={onProjectSelectionChange}
                    onInputChange={setProjectSearchText}
                    inputValue={projectSearchText}
                    value={selectedProjectName || formState.projectName || ''}
                    disabled={isProjectIdDisabled}
                />
                {isCreateNewCustomer && (
                    <div className='text-xs text-grey-80 mt-1'>Project is set to Create New because you are creating a new customer.</div>
                )}
                {hasPortfolioId && <div className='text-xs text-grey-80 mt-1'>Optional when Portfolio ID is provided</div>}
                {!hasPortfolioId && <div className='text-xs text-grey-80 mt-1'>Required when no Portfolio ID</div>}
            </div>

            <div className={styles.formInputSection}>
                <div className={styles.formInputSectionTitle}>
                    Project Name
                    {isProjectNameRequired && <span className='text-red-100'> *</span>}
                </div>
                <Input
                    value={formState.projectName || ''}
                    onChange={onProjectNameChange}
                    disabled={isProjectNameDisabled}
                    placeholder={isProjectNameDisabled ? 'Select "Create New" in Project ID to enable' : 'Enter project name'}
                />
                {isProjectNameDisabled && (
                    <div className='text-xs text-grey-80 mt-1'>Select &quot;Create New&quot; in Project ID to enable this field</div>
                )}
                {!isProjectNameDisabled && <div className='text-xs text-grey-80 mt-1'>Required when creating a new project</div>}
            </div>

            <div className={styles.formInputSection}>
                <div className={styles.formInputSectionTitle}>Is this an HLCA subscription?</div>
                <Checkbox
                    checked={formState.isHlcaProject === true}
                    onChange={onHlcaChange}
                    disabled={!hasPortfolioId}
                    label='HLCA subscription'
                />
                {!hasPortfolioId && (
                    <div className='text-xs text-grey-80 mt-1'>
                        {!hasPortfolioId ? 'Automatically checked when no Portfolio ID is provided' : 'Enable by providing a Portfolio ID'}
                    </div>
                )}
                {hasPortfolioId && <div className='text-xs text-grey-80 mt-1'>Editable when creating a new project with Portfolio ID</div>}
            </div>

            <div className={styles.formInputSection}>
                <div className={styles.formInputSectionTitle}>
                    Country <span className='text-red-100'> *</span>
                </div>
                <Autocomplete
                    options={['', ...Object.keys(countries).sort()]}
                    placeholder={'Search country name'}
                    prefixIcon={<Search />}
                    onChange={onCountryChange}
                    value={formState.country || ''}
                    onInputChange={setCountrySearchText}
                    inputValue={countrySearchText}
                    disabled={false}
                />
            </div>

            <div className={styles.formInputSection}>
                <div className={styles.formInputSectionTitle}>Run Cloud Jobs</div>
                <Checkbox
                    checked={formState.runCloudJobs ?? true}
                    onChange={(checked: boolean) => onFormChange({ runCloudJobs: checked })}
                    label='Run cloud jobs'
                />
                <div className='text-xs text-grey-80 mt-1'>Enable cloud job processing for this upload</div>
            </div>
        </div>
    );
};

export default BasicInformationAddSite;
